import React from 'react';
import { SelectOption } from 'components/common/input-components/select';
import { FormattedMessage } from 'react-intl';
import styled from 'types/theme.types';
import { BranchesOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import ControlledSelect from 'components/common/form-controlled/controlled-select';
import { setLink } from 'components/common/link';
import { links } from 'constants/external-links';

const SelectItemContainer = styled.div`
  display: flex;
  align-items: center;
  width: 200px;
`;

const ErrorIcon = styled(WarningOutlined)`
  height: 15px;
  width: 15px;
  margin-right: 5px;
  color: red;
`;

const VcsIcon = styled(BranchesOutlined)`
  height: 15px;
  width: 15px;
  margin-right: 5px;
`;

const ManualIcon = styled(EditOutlined)`
  height: 15px;
  width: 15px;
  margin-right: 5px;
  color: #7fa1c5;
`;

const getCleanUrl = (url: string) => {
  return url.replace(/^https?:\/\/[^/]+\//, '');
};

export interface VcsRepositoryData {
  httpsUrl: string;
  isManualInput?: boolean;
  name: string;
  vcsConnectionId?: string;
}

export interface VcsRepositories {
  repositories?: Array<VcsRepositoryData>;
  errors?: Array<any>;
}

export interface VcsRepositorySelectProps {
  data?: VcsRepositories;
  loading?: boolean;
  customActions?: React.ReactNode;
  form?: any;
  // When provided, filtering is delegated to the server (search-as-you-type) instead of filtering the
  // already-loaded options client-side. This is what lets large installations avoid full enumeration.
  onSearch?: (searchTerm: string) => void;
}

const VcsRepositorySelector: React.FC<VcsRepositorySelectProps> = ({
  data,
  loading = false,
  customActions,
  form,
  onSearch
}) => {
  const setVcsConnectionId = (_value: string, option: any) => {
    form.setValue('vcsConnectionId', option.data.vcsConnectionId);
  };

  return (
    <ControlledSelect
      form={form}
      showSearch={true}
      onSearch={onSearch}
      // Server-side search must not be narrowed again by antd's client-side filter.
      filterOption={onSearch ? false : undefined}
      onChange={setVcsConnectionId}
      loading={loading}
      listHeight={200}
      name="repository"
      placement="bottomLeft"
      info={{
        id: `vcs.repository-selector.field.info`,
        values: setLink(links.VCS.MANAGE_VCS)
      }}
      data-e2e="vcs-repository-selector"
      placeholder={
        data?.repositories?.length === 0 ? (
          <FormattedMessage id="vcs.repository-selector.no-repositories" />
        ) : (
          <FormattedMessage id="vcs.repository-selector.placeholder" />
        )
      }
      customActions={customActions}>
      {data?.repositories?.map((repository, index) => (
        <SelectOption
          data-e2e={`repository-option-${index}`}
          key={repository.httpsUrl}
          value={repository.httpsUrl}
          data={repository}>
          <SelectItemContainer>
            {repository.isManualInput ? <ManualIcon /> : <VcsIcon />}
            <span>{getCleanUrl(repository.httpsUrl)}</span>
          </SelectItemContainer>
        </SelectOption>
      ))}
      {data?.errors?.map((error, index) => (
        <SelectOption key={index} value={index} disabled={true}>
          <SelectItemContainer>
            <ErrorIcon />
            {error}
          </SelectItemContainer>
        </SelectOption>
      ))}
    </ControlledSelect>
  );
};

export default VcsRepositorySelector;
